import apiUrl from './api';
import { postWithAuth, getWithAuth, postStreamWithAuth } from '@/lib/httpAdapter';
import {
  PerformanceReviewBatchRequest,
  PerformanceReviewStreamEvent,
  PerformanceReviewBatchResponse,
  BatchSessionStatusResponse,
} from '@/types/performanceReviewTypes';

const endpoints = {
  listPerformanceReview: `${apiUrl}listPerformanceReview/`,
  createPerformanceReview: `${apiUrl}createPerformanceReview/`,
  createPerformanceReviewBatch: `${apiUrl}createPerformanceReviewBatch/`,
  createPerformanceReviewBatchStream: `${apiUrl}createPerformanceReviewBatchStream/`,
  checkBatchSessionStatus: `${apiUrl}checkBatchSessionStatus/`,
  fetchPerformanceReview: `${apiUrl}fetchPerformanceReview/`,
  deletePerformanceReview: `${apiUrl}deletePerformanceReview/`,
};

const listPerformanceReview = async ({
  OrgUUID,
  TeamUUID,
  StartDate,
  EndDate,
  seatsOnly,
}: {
  OrgUUID: string;
  TeamUUID?: string;
  StartDate?: string;
  EndDate?: string;
  /**
   * Team Insights' "Paid seats only" scope. Individual Insights feeds this list
   * to PerformanceDistributionChart, which prefers it over the dashboard's
   * aggregates — so without this the distribution keeps non-seat engineers in a
   * view labelled "Paid seats only".
   */
  seatsOnly?: boolean;
}) => {
  try {
    const payload: {
      OrgUUID: string;
      TeamUUID?: string;
      StartDate?: string;
      EndDate?: string;
      seats_only?: boolean;
    } = { OrgUUID };

    // Only sent when on, so an unfiltered request body stays byte-identical.
    if (seatsOnly) {
      payload.seats_only = true;
    }

    if (TeamUUID) {
      payload.TeamUUID = TeamUUID;
    }

    if (StartDate) {
      payload.StartDate = StartDate;
    }

    if (EndDate) {
      payload.EndDate = EndDate;
    }

    const response = await postWithAuth(
      endpoints.listPerformanceReview,
      payload
    );

    if (!response.ok) {
      throw new Error('Failed to list performance reviews');
    }

    const data = JSON.parse(await response.json());
    if (!data.Error) {
      return data;
    } else {
      throw new Error(data.Error);
    }
  } catch (error) {
    throw error;
  }
};

const createPerformanceReview = async ({
  OrgUUID,
  UserUUID,
  TeamUUID,
  TeamName,
  Months,
  Platform,
}: {
  OrgUUID: string;
  UserUUID: string;
  TeamUUID?: string | null;
  TeamName?: string | null;
  Months?: number;
  Platform?: string;
}) => {
  try {
    const payload: {
      OrgUUID: string;
      UserUUID: string;
      TeamUUID?: string | null;
      TeamName?: string | null;
      Months?: number;
      Platform?: string;
    } = { OrgUUID, UserUUID };

    if (TeamUUID && TeamUUID.trim() !== '') {
      payload.TeamUUID = TeamUUID;
    }
    if (TeamName && TeamName.trim() !== '') {
      payload.TeamName = TeamName;
    }

    if (Months) {
      payload.Months = Months;
    }
    if (Platform) {
      payload.Platform = Platform;
    }

    const response = await postWithAuth(
      endpoints.createPerformanceReview,
      payload
    );

    if (!response.ok) {
      throw new Error('Failed to create performance review');
    }

    const data = await response.json();
    if (!data.Error) {
      return data;
    } else {
      throw new Error(data.Error);
    }
  } catch (error) {
    throw error;
  }
};

const fetchPerformanceReview = async ({ UUID }: { UUID: string }) => {
  try {
    const payload: { UUID: string } = { UUID };

    const response = await postWithAuth(
      endpoints.fetchPerformanceReview,
      payload
    );

    if (!response.ok) {
      throw new Error('Failed to fetch performance review');
    }

    const data = JSON.parse(await response.json());

    if (!data.Error) {
      return data;
    } else {
      throw new Error(data.Error);
    }
  } catch (error) {
    throw error;
  }
};

const deletePerformanceReview = async ({ UUID }: { UUID: string }) => {
  try {
    const payload: { UUID: string } = { UUID };

    const response = await fetch(endpoints.deletePerformanceReview, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to delete performance review');
    }

    const data = JSON.parse(await response.json());

    if (!data.Error) {
      return data;
    } else {
      throw new Error(data.Error);
    }
  } catch (error) {
    throw error;
  }
};

const createPerformanceReviewBatch = async ({
  OrgUUID,
  TeamUUID,
  UserUUIDs,
  Months,
  MainModel,
  Platform,
  MaxConcurrency,
  ForTesting,
}: {
  OrgUUID: string;
  TeamUUID?: string;
  UserUUIDs?: string[];
  Months?: number;
  MainModel?: string;
  Platform?: string;
  MaxConcurrency?: number;
  ForTesting?: boolean;
}) => {
  try {
    const payload: {
      OrgUUID: string;
      TeamUUID?: string;
      UserUUIDs?: string[];
      Months?: number;
      MainModel?: string;
      Platform?: string;
      MaxConcurrency?: number;
      ForTesting?: boolean;
    } = { OrgUUID };

    if (TeamUUID) {
      payload.TeamUUID = TeamUUID;
    }

    if (UserUUIDs && UserUUIDs.length > 0) {
      payload.UserUUIDs = UserUUIDs;
    }

    if (Months) {
      payload.Months = Months;
    }

    if (MainModel) {
      payload.MainModel = MainModel;
    }

    if (Platform) {
      payload.Platform = Platform;
    }

    if (MaxConcurrency) {
      payload.MaxConcurrency = MaxConcurrency;
    }

    if (ForTesting !== undefined) {
      payload.ForTesting = ForTesting;
    }

    const response = await postWithAuth(
      endpoints.createPerformanceReviewBatch,
      payload
    );

    if (!response.ok) {
      throw new Error('Failed to create batch performance reviews');
    }

    const data = await response.json();
    if (!data.Error) {
      return data;
    } else {
      throw new Error(data.Error);
    }
  } catch (error) {
    throw error;
  }
};

export class PerformanceReviewStreamAPI {
  static async startBatchStream(
    request: PerformanceReviewBatchRequest,
    onUpdate: (event: PerformanceReviewStreamEvent) => void,
    onError: (error: string) => void,
    onComplete: (result: PerformanceReviewBatchResponse) => void
  ): Promise<void> {
    // Create a local abort controller for this specific stream (not shared)
    const abortController = new AbortController();

    try {
      const response = await postStreamWithAuth(
        endpoints.createPerformanceReviewBatchStream,
        request,
        { signal: abortController.signal }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body available');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          const lines = buffer.split('\n');

          for (let i = 0; i < lines.length - 1; i++) {
            const line = lines[i].trim();
            if (line) {
              try {
                const eventData: PerformanceReviewStreamEvent =
                  JSON.parse(line);
                onUpdate(eventData);

                if (eventData.is_complete) {
                  if (eventData.stage === 'completed' && eventData.data) {
                    const result: PerformanceReviewBatchResponse = {
                      org_uuid: eventData.data.org_uuid || '',
                      total_users: eventData.data.total_users || 0,
                      successful: eventData.data.successful || 0,
                      failed: eventData.data.failed || 0,
                      skipped: eventData.data.skipped || 0,
                      results: eventData.data.results || [],
                      total_duration_seconds:
                        eventData.data.total_duration_seconds || 0,
                    };
                    onComplete(result);
                    return;
                  } else if (eventData.stage === 'error') {
                    onError(eventData.error || 'Unknown error occurred');
                    return;
                  }
                }
              } catch {
                // Skip invalid JSON lines
              }
            }
          }

          buffer = lines[lines.length - 1] || '';
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      onError(
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  }

  static generateSessionId(): string {
    return `perf-review-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  static async checkSessionStatus(
    sessionId: string
  ): Promise<BatchSessionStatusResponse> {
    try {
      const url = `${endpoints.checkBatchSessionStatus}?session_id=${encodeURIComponent(sessionId)}`;
      const response = await getWithAuth(url);

      if (!response.ok) {
        if (response.status === 404) {
          return {
            active: false,
            error: 'Session not found or expired',
            shouldClearStorage: true,
          };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        active: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        shouldClearStorage: false,
      };
    }
  }
}

export {
  listPerformanceReview,
  createPerformanceReview,
  createPerformanceReviewBatch,
  fetchPerformanceReview,
  deletePerformanceReview,
};
